"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { RegisterDtoSchema, getPasswordStrength } from '@/lib/validations/auth.validation';
import { authService } from '@/services/auth.service';
import { Eye, EyeOff, AlertCircle, Check, X, ArrowRight, Users, Building2, Mail, Lock, User } from 'lucide-react';
import { UserRole } from '@/models/User';
import { motion, AnimatePresence } from 'framer-motion';

export default function SignupPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    role: UserRole.STUDENT,
  });

  // Redirect if already logged in
  useEffect(() => {
    if (authService.isAuthenticated()) {
      const role = authService.getUserRole();
      if (role) {
        switch (role.toLowerCase()) {
          case 'student':
            router.push('/student/dashboard');
            return;
          case 'company':
            router.push('/company/dashboard');
            return;
          case 'teacher':
            router.push('/teacher/dashboard');
            return;
          case 'admin':
            router.push('/admin/dashboard');
            return;
        }
      }
    }
  }, [router]);

  const passwordStrength = getPasswordStrength(formData.password);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleRoleChange = (role: UserRole.STUDENT | UserRole.COMPANY) => {
    setFormData(prev => ({ ...prev, role }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setIsLoading(true);

    try {
      const validatedData = RegisterDtoSchema.parse(formData);
      await authService.register(validatedData);
      router.push('/auth/login?registered=true');
    } catch (err: any) {
      if (err.errors) {
        const errors: Record<string, string> = {};
        err.errors.forEach((error: any) => {
          errors[error.path[0]] = error.message;
        });
        setFieldErrors(errors);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-red-500';
    }
  };

  const getStrengthWidth = () => {
    switch (passwordStrength) {
      case 'strong': return 'w-full';
      case 'medium': return 'w-2/3';
      default: return 'w-1/3';
    }
  };

  const passwordRequirements = [
    { met: formData.password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(formData.password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(formData.password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(formData.password), text: 'One number' },
  ];

  return (
    <div className="min-h-screen flex">
      <motion.div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid.svg')] bg-repeat"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link href="/">
            <div className="relative w-48 h-16">
              <Image src="/logo.svg" alt="Learn2Link" fill className="object-contain" priority />
            </div>
          </Link>

          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                Join Learn2Link<br />Today
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                Create your account and start your journey towards success. Connect with opportunities and enhance your skills.
              </p>
            </motion.div>

            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-4 text-white/90">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Check className="w-6 h-6" />
                </div>
                <span className="text-lg">Access to premium courses</span>
              </div>
              <div className="flex items-center gap-4 text-white/90">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Check className="w-6 h-6" />
                </div>
                <span className="text-lg">Connect with top companies</span>
              </div>
              <div className="flex items-center gap-4 text-white/90">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Check className="w-6 h-6" />
                </div>
                <span className="text-lg">Personalized learning path</span>
              </div>
            </motion.div>
          </div>

          <p className="text-white/60 text-sm">© 2025 Learn2Link. All rights reserved.</p>
        </div>
      </motion.div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <motion.div 
          className="w-full max-w-md py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="lg:hidden mb-8 text-center">
            <Link href="/">
              <div className="relative w-36 h-12 mx-auto">
                <Image src="/logo.svg" alt="Learn2Link" fill className="object-contain" priority />
              </div>
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h2>
            <p className="text-gray-600">Get started with your free account</p>
          </div>

          {error && (
            <motion.div 
              className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </motion.div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">I am:</label>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                type="button"
                onClick={() => handleRoleChange(UserRole.STUDENT)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.role === UserRole.STUDENT
                    ? 'border-[#6059f7] bg-[#6059f7]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Users className={`w-6 h-6 mx-auto mb-2 ${
                  formData.role === UserRole.STUDENT ? 'text-[#6059f7]' : 'text-gray-400'
                }`} />
                <div className={`font-semibold text-sm ${
                  formData.role === UserRole.STUDENT ? 'text-[#6059f7]' : 'text-gray-700'
                }`}>
                  Student
                </div>
              </motion.button>

              <motion.button
                type="button"
                onClick={() => handleRoleChange(UserRole.COMPANY)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.role === UserRole.COMPANY
                    ? 'border-[#6059f7] bg-[#6059f7]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Building2 className={`w-6 h-6 mx-auto mb-2 ${
                  formData.role === UserRole.COMPANY ? 'text-[#6059f7]' : 'text-gray-400'
                }`} />
                <div className={`font-semibold text-sm ${
                  formData.role === UserRole.COMPANY ? 'text-[#6059f7]' : 'text-gray-700'
                }`}>
                  Company
                </div>
              </motion.button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full pl-11 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                      fieldErrors.firstName
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#6059f7]'
                    }`}
                    placeholder="John"
                  />
                </div>
                {fieldErrors.firstName && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full pl-11 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                      fieldErrors.lastName
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#6059f7]'
                    }`}
                    placeholder="Doe"
                  />
                </div>
                {fieldErrors.lastName && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>

            <AnimatePresence>
              {formData.role === UserRole.COMPANY && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      required={formData.role === UserRole.COMPANY}
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className={`w-full pl-11 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                        fieldErrors.companyName
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-[#6059f7]'
                      }`}
                      placeholder="Acme Corporation"
                    />
                  </div>
                  {fieldErrors.companyName && (
                    <p className="mt-2 text-xs text-red-600">{fieldErrors.companyName}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                    fieldErrors.email
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#6059f7]'
                  }`}
                  placeholder="you@example.com"
                />
              </div>
              {fieldErrors.email && (
                <p className="mt-2 text-xs text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-12 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                    fieldErrors.password
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#6059f7]'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <AnimatePresence>
                {formData.password && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${getStrengthColor()} ${getStrengthWidth()}`} />
                      </div>
                      <span className={`text-xs font-semibold ${
                        passwordStrength === 'strong' ? 'text-green-600' :
                        passwordStrength === 'medium' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {passwordStrength.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {passwordRequirements.map((req, index) => (
                        <div key={index} className="flex items-center gap-1.5 text-xs">
                          {req.met ? (
                            <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          )}
                          <span className={req.met ? 'text-green-600' : 'text-gray-500'}>
                            {req.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-12 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                    fieldErrors.confirmPassword
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#6059f7]'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="mt-2 text-xs text-red-600">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            <motion.button
              type="submit"
              disabled={isLoading || passwordStrength === 'weak'}
              className="w-full py-3.5 bg-[#6059f7] text-white rounded-lg font-semibold hover:bg-[#5048e8] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#6059f7]/20 mt-6"
              whileHover={{ scale: (isLoading || passwordStrength === 'weak') ? 1 : 1.02 }}
              whileTap={{ scale: (isLoading || passwordStrength === 'weak') ? 1 : 0.98 }}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>

            <div className="text-center text-sm text-gray-600 mt-6">
              Already have an account?{' '}
              <Link href="/auth/login" className="text-[#6059f7] font-semibold hover:text-[#6059f7]/80 transition-colors">
                Sign in
              </Link>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
